// MIDAS V3 — New-chat picker readiness cluster (DR-5)
//
// Renders, LEFT of a machine row's existing RUNNING chip, a compact MIDAS
// data-readiness cluster:
//   • blue-dot "n docs" chip  (muted "no docs" when none — but no-docs
//     machines are grouped/de-emphasized by the host, so in practice this
//     always shows ≥1 doc)
//   • gold star when the model's manufacturer is an OEM partner
//   • an (i) affordance that opens a docs popover on HOVER and on FOCUS
//
// LIVE / telemetry is deliberately NOT shown here — the RUNNING chip already
// owns live status (spec §6, rule 2). This cluster is informational only:
// there is no "Ask MIDAS" CTA (selecting the machine is the action).
//
// Derivations come ONLY from the shared util
// customer/machinery/utils/midasReadiness.ts — never re-derived inline.
//
// Design source of truth: Paper file 01KXFWWD7E8X1W15NG04Z5RCQM, page
// "v2 — in current UI", artboard "v2-4 — MIDAS picker" (get_jsx, inline
// styles). Greys are mapped onto the picker's own --midas-v3-* theme vars so
// the cluster stays theme-aware (cream p-machineid AND p-claude); the blue
// doc-signal and gold OEM star are semantic signal colours (same in both
// themes, mirroring the DR-2 catalog row + shared OemStar).

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { resolveV3PaletteClass } from "../../midasV3Palette";
import { Machine } from "../../../machinery/models/models";
import {
  hasDocs as hasDocsUtil,
  isOptInDocType,
  litDocTypes,
  oemDotLit,
  PopupDocType,
  totalDocs,
} from "../../../machinery/utils/midasReadiness";
import OemStar from "../../../machinery/components/OemStar";

// Semantic signal colours — not part of the --midas-v3 theme token set
// (mirrors CatalogReadinessRow's litMidasDot + OemStar's default gold).
const DOC_BLUE = "#3498DB";
const OEM_GOLD = "#B98A1F";

// Fixed doc-type lanes in canonical display order (matches the Paper popover).
// Presence per lane comes from the shared util (`litDocTypes`) — NEVER derived
// inline — so the picker popover can never disagree with the machinery/profile
// popup about the same machine. `key` also maps to the i18n label.
// `oemNodeDocs` (DR-6) and `componentDocs` (READINESS-COMPONENT-DOCS-1) are
// OPT-IN: each is dropped from the lane list unless lit, so no machine grows a
// permanent fifth/sixth dashed lane. The rule itself lives in the shared util
// (`isOptInDocType`) — never re-listed here.
const DOC_LANE_ORDER: PopupDocType[] = [
  "manual",
  "electrical",
  "alarms",
  "specSheet",
  "oemNodeDocs",
  "componentDocs",
];

interface NewChatReadinessClusterProps {
  machine: Machine;
}

const NewChatReadinessCluster: React.FC<NewChatReadinessClusterProps> = ({ machine }) => {
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string, opts?: Record<string, unknown>) =>
    translate(k as any, { defaultValue: f, ...(opts || {}) }) as string;

  const catalogModel = machine.catalogModel;
  const readiness = catalogModel?.readiness ?? null;
  const docCount = totalDocs(readiness);
  const hasDocs = hasDocsUtil(readiness);
  const isOem = catalogModel ? oemDotLit(catalogModel) : false;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const POPOVER_W = 300;

  // Position the portalled popover from the (i) anchor rect. Opens to the
  // LEFT of the (i) (it sits near the modal's right edge) and clamps to the
  // viewport so it never renders off-screen or gets clipped by the list's
  // own overflow.
  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.left - POPOVER_W - 8;
    if (left < 8) left = Math.min(r.right + 8, window.innerWidth - POPOVER_W - 8);
    if (left < 8) left = 8;
    let top = r.top - 6;
    // Keep within the viewport vertically (rough guard; popover auto-sizes).
    const maxTop = window.innerHeight - 220;
    if (top > maxTop) top = Math.max(8, maxTop);
    if (top < 8) top = 8;
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => reposition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, reposition]);

  // ── doc-type lanes for the popover ─────────────────────────
  // Presence comes ONLY from the shared util's `litDocTypes` (which itself
  // handles the docCounts-absent fallback via exact-enum docTypes matching),
  // so the picker popover and the machinery/profile popup stay in lock-step.
  const lanes = useMemo(() => {
    const lit = new Set<PopupDocType>(litDocTypes(readiness));
    return DOC_LANE_ORDER.filter((key) => lit.has(key) || !isOptInDocType(key)).map((key) => ({
      key,
      present: lit.has(key),
    }));
  }, [readiness]);
  const anyLanePresent = lanes.some((l) => l.present);
  const languages = readiness?.languages || [];

  const laneLabel = (key: string): string => {
    switch (key) {
      case "manual":
        return t("midas.v3.newChatDialog.readiness.popover.docType.manual", "Operator manual");
      case "electrical":
        return t("midas.v3.newChatDialog.readiness.popover.docType.electrical", "Electrical docs");
      case "alarms":
        return t("midas.v3.newChatDialog.readiness.popover.docType.alarms", "Alarm codes");
      case "oemNodeDocs":
        return t("midas.v3.newChatDialog.readiness.popover.docType.oemNodeDocs", "OEM node docs");
      case "componentDocs":
        return t("midas.v3.newChatDialog.readiness.popover.docType.componentDocs", "Component docs");
      case "specSheet":
      default:
        return t("midas.v3.newChatDialog.readiness.popover.docType.specSheet", "Spec sheet");
    }
  };

  const modelName =
    (catalogModel?.modelName || machine.model || "").trim() || machine.model || "";

  // Stop the (i) interactions from bubbling to the row <button> (which would
  // select the machine and close the dialog).
  const swallow = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const docChipLabel = hasDocs
    ? docCount === 1
      ? t("midas.v3.newChatDialog.readiness.docChipOne", "{{count}} doc", { count: docCount })
      : t("midas.v3.newChatDialog.readiness.docsChip", "{{count}} docs", { count: docCount })
    : t("midas.v3.newChatDialog.readiness.noDocs", "no docs");

  const infoAria = t("midas.v3.newChatDialog.readiness.infoAria", "MIDAS document details");

  // Stable id per machine row so the (i) can aria-describedby/aria-controls the
  // tooltip node.
  const tooltipId = `midas-readiness-tip-${machine.id}`;

  // Mirror the dialog's theme onto the body-portalled popover so every
  // --midas-v3-* var, font var, and `.mono` class resolves — they're scoped to
  // `.midas-v3-root.p-*`. Without this wrapper the popover renders unstyled
  // (invisible checkmarks) in BOTH themes. Same detection NewChatDialogV3 uses
  // for its own body portal; `background: transparent` defeats the opaque cream
  // fill `.midas-v3-root` would otherwise paint (mirrors the dialog wrapper).
  // `resolveV3PaletteClass()` replaced a 2-way inline sniff that fell through
  // to `p-machineid` for any palette other than claude — brand-blue popover on
  // a teal/slate page (MIDAS-V3-PALETTES-TEAL-SLATE).
  const v3Theme = resolveV3PaletteClass();

  const popover =
    open && coords
      ? ReactDOM.createPortal(
          <div
            className={`midas-v3-root ${v3Theme}`}
            style={{ background: "transparent" }}
          >
            <div
              id={tooltipId}
              role="tooltip"
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                width: POPOVER_W,
                zIndex: 1400,
                background: "var(--midas-v3-card)",
                border: "1px solid var(--midas-v3-border)",
                borderRadius: 12,
                boxShadow: "0 10px 34px rgba(0,0,0,0.18)",
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                fontFamily: "var(--font-sans)",
                color: "var(--midas-v3-ink)",
                pointerEvents: "none",
              }}
            >
            {/* Header */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  color: "var(--midas-v3-accent)",
                }}
              >
                {modelName
                  ? `${modelName} · ${t("midas.v3.newChatDialog.readiness.popover.eyebrow", "MODEL DATA")}`
                  : t("midas.v3.newChatDialog.readiness.popover.eyebrow", "MODEL DATA")}
              </div>
              {modelName && (
                <div
                  className="mono"
                  style={{ fontSize: 9.5, color: "var(--midas-v3-faint)" }}
                >
                  {t(
                    "midas.v3.newChatDialog.readiness.popover.subtitle",
                    "Applies to every {{model}} unit",
                    { model: modelName }
                  )}
                </div>
              )}
            </div>

            {/* Doc-type lanes */}
            <div
              style={{
                borderTop: "1px solid var(--midas-v3-border-soft)",
                paddingTop: 10,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {anyLanePresent ? (
                lanes.map((lane) => (
                  <div
                    key={lane.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: lane.present ? 1 : 0.5,
                    }}
                  >
                    {lane.present ? (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ flexShrink: 0 }}
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          fill="none"
                          stroke="var(--midas-v3-green)"
                          strokeWidth={3}
                        />
                      </svg>
                    ) : (
                      <span
                        className="mono"
                        style={{
                          width: 11,
                          flexShrink: 0,
                          textAlign: "center",
                          fontSize: 10,
                          color: "var(--midas-v3-faint)",
                        }}
                      >
                        —
                      </span>
                    )}
                    <span
                      style={{
                        flex: 1,
                        fontSize: 12.5,
                        lineHeight: "16px",
                        color: lane.present
                          ? "var(--midas-v3-ink)"
                          : "var(--midas-v3-muted)",
                      }}
                    >
                      {laneLabel(lane.key)}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M20 6L9 17l-5-5" fill="none" stroke="var(--midas-v3-green)" strokeWidth={3} />
                  </svg>
                  <span style={{ flex: 1, fontSize: 12.5, lineHeight: "16px", color: "var(--midas-v3-ink)" }}>
                    {docCount === 1
                      ? t("midas.v3.newChatDialog.readiness.popover.genericOne", "{{count}} document", { count: docCount })
                      : t("midas.v3.newChatDialog.readiness.popover.generic", "{{count}} documents", { count: docCount })}
                  </span>
                </div>
              )}
            </div>

            {/* Languages metadata (informational only — never a gate) */}
            {languages.length > 0 && (
              <div
                className="mono"
                style={{
                  fontSize: 9.5,
                  color: "var(--midas-v3-faint)",
                  letterSpacing: "0.02em",
                }}
              >
                {t("midas.v3.newChatDialog.readiness.popover.languagesLabel", "Languages")}
                {" · "}
                {languages.join(" · ")}
              </div>
            )}

            {/* Disclaimer — present in every state */}
            <div
              className="mono"
              style={{
                borderTop: "1px solid var(--midas-v3-border-soft)",
                paddingTop: 9,
                fontSize: 9,
                lineHeight: "13px",
                color: "var(--midas-v3-faint)",
              }}
            >
              {t(
                "midas.v3.newChatDialog.readiness.popover.disclaimer",
                "doc language ≠ chat language — MIDAS answers in yours"
              )}
            </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        flexShrink: 0,
      }}
    >
      {/* doc-count chip */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {hasDocs && (
          <span
            aria-hidden="true"
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: DOC_BLUE,
              flexShrink: 0,
            }}
          />
        )}
        <span
          className="mono"
          style={{
            fontSize: 10,
            lineHeight: "12px",
            color: hasDocs ? "var(--midas-v3-muted)" : "var(--midas-v3-faint)",
            whiteSpace: "nowrap",
          }}
        >
          {docChipLabel}
        </span>
      </div>

      {/* gold OEM star */}
      {isOem && <OemStar size={11} fill={OEM_GOLD} />}

      {/* (i) affordance — hover + focus open the docs popover */}
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label={infoAria}
        aria-expanded={open}
        aria-controls={open ? tooltipId : undefined}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseDown={swallow}
        onClick={(e) => {
          swallow(e);
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            e.preventDefault();
            setOpen((o) => !o);
          } else if (e.key === "Escape") {
            // Stop the dialog's document-level Esc handler from firing (which
            // would call onClose() and close the whole dialog). Esc on an open
            // popover must only close the popover.
            e.stopPropagation();
            setOpen(false);
          }
        }}
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          cursor: "default",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 600,
          lineHeight: "12px",
          outline: "none",
          background: open ? "var(--midas-v3-card)" : "transparent",
          border: `1.4px solid ${open ? "var(--midas-v3-accent)" : "var(--midas-v3-border)"}`,
          color: open ? "var(--midas-v3-accent)" : "var(--midas-v3-faint)",
          transition: "color .12s, border-color .12s, background-color .12s",
        }}
      >
        i
      </span>
      {popover}
    </div>
  );
};

export default NewChatReadinessCluster;
